'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CircleCalculator() {
  const t = useTranslations("tools.circleCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    radius: '',
    diameter: '',
    circumference: '',
    area: ''
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const PI = Math.PI;

  const formatNumber = (num: number): string => {
    if (num === 0) return '0';
    if (Math.abs(num) < 0.0001 || Math.abs(num) > 1000000) {
      return num.toExponential(6);
    }
    return num.toFixed(6).replace(/\.?0+$/, '');
  };

  const calculateFromRadius = (r: number) => {
    return {
      radius: r,
      diameter: 2 * r,
      circumference: 2 * PI * r,
      area: PI * r * r
    };
  };

  const calculateFromDiameter = (d: number) => {
    const r = d / 2;
    return calculateFromRadius(r);
  };

  const calculateFromCircumference = (c: number) => {
    const r = c / (2 * PI);
    return calculateFromRadius(r);
  };

  const calculateFromArea = (a: number) => {
    const r = Math.sqrt(a / PI);
    return calculateFromRadius(r);
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs({
      radius: field === 'radius' ? value : '',
      diameter: field === 'diameter' ? value : '',
      circumference: field === 'circumference' ? value : '',
      area: field === 'area' ? value : ''
    });
    setError('');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const radius = parseFloat(inputs.radius);
    const diameter = parseFloat(inputs.diameter);
    const circumference = parseFloat(inputs.circumference);
    const area = parseFloat(inputs.area);

    let calculatedResults;

    if (!isNaN(radius) && radius > 0) {
      calculatedResults = calculateFromRadius(radius);
    } else if (!isNaN(diameter) && diameter > 0) {
      calculatedResults = calculateFromDiameter(diameter);
    } else if (!isNaN(circumference) && circumference > 0) {
      calculatedResults = calculateFromCircumference(circumference);
    } else if (!isNaN(area) && area > 0) {
      calculatedResults = calculateFromArea(area);
    } else {
      setError(t('errorMessage'));
      setResults(null);
      return;
    }

    setResults(calculatedResults);
  };

  const handleClear = () => {
    setInputs({
      radius: '',
      diameter: '',
      circumference: '',
      area: ''
    });
    setResults(null);
    setError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.75rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.5rem' }
  );

  const formGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: '#f8fafc',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
      minWidth: '140px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minWidth: '140px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    borderLeft: '4px solid #dc2626',
    display: error ? 'block' : 'none'
  };

  const formulaInfoStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      borderLeft: '4px solid #f59e0b'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const formulaTitleStyle = getResponsiveStyle(
    { color: '#92400e', marginBottom: '0.75rem', fontWeight: 600 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const formulaTextStyle = getResponsiveStyle(
    { color: '#78350f', margin: '0.5rem 0', fontFamily: "'Courier New', monospace" },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.5rem' }
  );

  const resultsHeaderStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }
  );

  const resultItemStyle = {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #2563eb',
    transition: 'all 0.3s ease'
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em'
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const circleVisualStyle = getResponsiveStyle(
    {
      textAlign: 'center' as const,
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '1rem'
    },
    { marginTop: '1.5rem', padding: '1.5rem' },
    { marginTop: '2rem', padding: '2rem', borderRadius: '1.25rem' },
    { marginTop: '2rem', padding: '2rem', borderRadius: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const referenceStyle = {
    marginBottom: '0.75rem'
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={toolSubtitleStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={calculatorFormStyle}>
            <div style={errorMessageStyle}>{error}</div>

            <form onSubmit={handleCalculate}>
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label htmlFor="radius" style={labelStyle}>{t('radiusLabel')}</label>
                  <input
                    type="number"
                    id="radius"
                    step="any"
                    placeholder={t('radiusPlaceholder')}
                    value={inputs.radius}
                    onChange={(e) => handleInputChange('radius', e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="diameter" style={labelStyle}>{t('diameterLabel')}</label>
                  <input
                    type="number"
                    id="diameter"
                    step="any"
                    placeholder={t('diameterPlaceholder')}
                    value={inputs.diameter}
                    onChange={(e) => handleInputChange('diameter', e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="circumference" style={labelStyle}>{t('circumferenceLabel')}</label>
                  <input
                    type="number"
                    id="circumference"
                    step="any"
                    placeholder={t('circumferencePlaceholder')}
                    value={inputs.circumference}
                    onChange={(e) => handleInputChange('circumference', e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="area" style={labelStyle}>{t('areaLabel')}</label>
                  <input
                    type="number"
                    id="area"
                    step="any"
                    placeholder={t('areaPlaceholder')}
                    value={inputs.area}
                    onChange={(e) => handleInputChange('area', e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
                <button type="button" onClick={handleClear} style={clearButtonStyle}>{t('clearButton')}</button>
              </div>
            </form>

            <div style={formulaInfoStyle}>
              <h4 style={formulaTitleStyle}>{t('formulaTitle')}</h4>
              <p style={formulaTextStyle}><strong>{t('formulaDiameter')}</strong> d = 2r</p>
              <p style={formulaTextStyle}><strong>{t('formulaCircumference')}</strong> C = 2πr = πd</p>
              <p style={formulaTextStyle}><strong>{t('formulaArea')}</strong> A = πr²</p>
              <p style={formulaTextStyle}><strong>{t('formulaPi')}</strong> ≈ 3.141592653589793</p>
            </div>
          </div>

          <div style={resultsStyle}>
            <h3 style={resultsHeaderStyle}>{t('resultsTitle')}</h3>

            <div style={resultsGridStyle}>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultRadius')}</span>
                <span style={resultValueStyle}>{results ? formatNumber(results.radius) : '-'}</span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultDiameter')}</span>
                <span style={resultValueStyle}>{results ? formatNumber(results.diameter) : '-'}</span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultCircumference')}</span>
                <span style={resultValueStyle}>{results ? formatNumber(results.circumference) : '-'}</span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultArea')}</span>
                <span style={resultValueStyle}>{results ? formatNumber(results.area) : '-'}</span>
              </div>
            </div>

            <div style={circleVisualStyle}>
              <svg style={{ maxWidth: '300px', height: 'auto', margin: '0 auto', display: 'block' }} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
                <circle cx="150" cy="150" r="100" fill="none" stroke="#2563eb" strokeWidth="3"/>
                <line x1="150" y1="150" x2="250" y2="150" stroke="#7c3aed" strokeWidth="2"/>
                <circle cx="150" cy="150" r="3" fill="#7c3aed"/>
                <text x="200" y="145" fill="#7c3aed" fontSize="14" fontWeight="bold">r</text>
                <line x1="50" y1="150" x2="250" y2="150" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5"/>
                <text x="145" y="170" fill="#f59e0b" fontSize="14" fontWeight="bold">d</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li>{t('howToUseStep1')}</li>
          <li>{t('howToUseStep2')}</li>
          <li>{t('howToUseStep3')}</li>
          <li>{t('howToUseStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li><strong>{t('detailsEducation')}</strong> {t('detailsEducationText')}</li>
          <li><strong>{t('detailsEngineering')}</strong> {t('detailsEngineeringText')}</li>
          <li><strong>{t('detailsArchitecture')}</strong> {t('detailsArchitectureText')}</li>
          <li><strong>{t('detailsGraphicDesign')}</strong> {t('detailsGraphicDesignText')}</li>
          <li><strong>{t('detailsEveryday')}</strong> {t('detailsEverydayText')}</li>
        </ul>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          <a href="https://www.symbolab.com/calculator/geometry/circle" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference1')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.graphcalc.com/circle-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference2')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://engineersguidebook.com/circumference-of-a-circle-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference3')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.smappen.com/blog/how-a-map-radius-calculator-can-transform-your-business/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference4')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.smashingmagazine.com/2016/01/why-i-moved-from-a-square-to-a-circle-calculator-interface-design/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference5')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://mechanicalc.com/calculators/mohrs-circle/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference6')}
          </a>
        </div>
      </section>
    </div>
  );
}